import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useRunLogs } from 'app/context/RunLogsContext';
import socketServiceManager from 'app/socketService';



interface DetailedRunLog {
  runId: string;
  status: string;
  message: string;
  timestamp: string;
  progress?: number;
  totalRecords?: number;
  processedRecords?: number;
  durationMs?: number;
  startTimestamp?: string;
  endTimestamp?: string;
  actionId?: string;
  rawData?: any;
}

const SocketMonitoringPanel: React.FC = () => {
  const { socketConnected, realTimeRunLogs } = useRunLogs();
  const [detailedRunLogs, setDetailedRunLogs] = useState<DetailedRunLog[]>([]);

  // Process and store detailed run log information
  useEffect(() => {
    if (realTimeRunLogs.length > 0) {
      const latestRunLog = realTimeRunLogs[realTimeRunLogs.length - 1];
      
      // Handle nested message structure from backend
      let eventData = latestRunLog;
      if (latestRunLog.message && typeof latestRunLog.message === 'object') {
        eventData = latestRunLog.message;
        console.log('📦 Extracted nested message data:', eventData);
      }

              const runId = eventData.run_log_id || eventData.runId || eventData.id;
      
      if (runId) {
        const detailedRunLog: DetailedRunLog = {
          runId,
          status: eventData.status || 'unknown',
          message: eventData.message || 'No message',
          timestamp: eventData.timestamp || new Date().toISOString(),
          progress: eventData.progress,
          totalRecords: eventData.total_records,
          processedRecords: eventData.processed_records,
          durationMs: eventData.duration_ms,
          startTimestamp: eventData.start_timestamp,
          endTimestamp: eventData.end_timestamp,
          actionId: (eventData as any).action_id,
          rawData: latestRunLog // Store the original data for debugging
        };

        setDetailedRunLogs(prev => {
          const existingIndex = prev.findIndex(log => log.runId === runId);
          if (existingIndex >= 0) {
            // Update existing entry
            const updated = [...prev];
            updated[existingIndex] = detailedRunLog;
            return updated;
          } else {
            // Add new entry
            return [detailedRunLog, ...prev.slice(0, 9)]; // Keep last 10
          }
        });


      }
    }
  }, [realTimeRunLogs]);



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <PlayArrowIcon color="primary" />;
      case 'completed':
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return <StopIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'primary';
      case 'completed':
      case 'success':
        return 'success';
      case 'error':
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleTimeString();
  };

  const clearHistory = () => {
    setDetailedRunLogs([]);
    try {
      const runLogsService = socketServiceManager.getService('RunLogsSocketService');
      if (runLogsService) {
        runLogsService.clearEventHistory();
      }
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: socketConnected ? 'success.main' : 'error.main',
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Connection Status */}
        <Alert 
          severity={socketConnected ? 'success' : 'error'} 
          sx={{ mb: 2 }}
        >
          Socket is {socketConnected ? 'connected and receiving updates' : 'disconnected'}
        </Alert>

          {/* Detailed Run Logs */}
          <Typography variant="h6" gutterBottom>
            Detailed Run Logs ({detailedRunLogs.length})
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
            {detailedRunLogs.map((runLog, index) => (
              <Paper key={`${runLog.runId}-${index}`} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusIcon(runLog.status)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {runLog.runId.substring(0, 8)}...
                      </Typography>
                      <Chip
                        label={runLog.status}
                        color={getStatusColor(runLog.status) as any}
                        size="small"
                      />
                      {runLog.actionId && (
                        <Chip
                          label={`Action: ${runLog.actionId}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {formatTimestamp(runLog.timestamp)}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                      <strong>Message:</strong> {runLog.message}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      {runLog.progress !== undefined && (
                        <Box sx={{ minWidth: 200 }}>
                          <Typography variant="caption" color="text.secondary">
                            Progress
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={runLog.progress} 
                            sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                          />
                          <Typography variant="caption">
                            {runLog.progress}%
                          </Typography>
                        </Box>
                      )}
                      
                      {runLog.totalRecords && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Records: {runLog.processedRecords || 0} / {runLog.totalRecords}
                          </Typography>
                        </Box>
                      )}
                      
                      {runLog.durationMs && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Duration: {formatDuration(runLog.durationMs)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      {runLog.startTimestamp && (
                        <Chip
                          icon={<TimerIcon />}
                          label={`Start: ${formatTimestamp(runLog.startTimestamp)}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {runLog.endTimestamp && (
                        <Chip
                          icon={<TimerIcon />}
                          label={`End: ${formatTimestamp(runLog.endTimestamp)}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ 
                      mt: 1, 
                      p: 1, 
                      backgroundColor: 'grey.50', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Raw Data:
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        display: 'block', 
                        mt: 0.5, 
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        wordBreak: 'break-all'
                      }}>
                        {JSON.stringify(runLog.rawData, null, 2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      </Paper>
    );
  };

export default SocketMonitoringPanel; 